import { useState, useEffect } from 'react'
import { X, Save } from 'lucide-react'
import { Modal, Button } from '@/components/common'
import { API_BASE_URL, API_ENDPOINTS } from '@/constants'
import { storageService } from '@/services'

const CATTLE_TYPES = [
  { value: 'BREEDING_COW', label: 'แม่พันธุ์' },
  { value: 'PREGNANT_COW', label: 'แม่ท้อง' },
  { value: 'BREEDING_BULL', label: 'พ่อพันธุ์' },
  { value: 'FATTENING_CATTLE', label: 'โคขุน' },
  { value: 'CALF', label: 'ลูกโค' },
  { value: 'YOUNG_CATTLE', label: 'โครุ่น' },
]

interface Cow {
  id: string
  name: string
  cowCode: string
  cattleType: string
}

interface WeightRecord {
  id: string
  cowId: string
  cowName: string
  cowCode: string
  cattleType: string
  date: string
  weight: number
  notes?: string
}

interface CowWeightFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: WeightRecord[], selectedDate: Date) => void
  selectedDate: Date
  defaultCattleType?: string
}

interface CowWeightInput {
  cowId: string
  cowName: string
  cowCode: string
  cattleType: string
  weight: number
  notes: string
  existingRecordId?: string
}

export default function CowWeightFormModal({
  isOpen,
  onClose,
  onSave,
  selectedDate,
  defaultCattleType = '',
}: CowWeightFormModalProps) {
  const [selectedCattleType, setSelectedCattleType] = useState(defaultCattleType)
  const [formDate, setFormDate] = useState(selectedDate)
  const [cowWeights, setCowWeights] = useState<CowWeightInput[]>([])
  const [allCows, setAllCows] = useState<Cow[]>([])
  const [isLoadingCows, setIsLoadingCows] = useState(false)

  // Update form date when selectedDate prop changes
  useEffect(() => {
    setFormDate(selectedDate)
  }, [selectedDate])

  // Fetch cows from API
  useEffect(() => {
    fetchCows()
  }, [])

  // Fetch weight records when date or cattleType changes
  useEffect(() => {
    if (formDate && selectedCattleType && allCows.length > 0) {
      fetchWeightRecordsForDate(formDate)
    }
  }, [formDate, selectedCattleType])

  const fetchWeightRecordsForDate = async (date: Date) => {
    try {
      const token = storageService.getAuthToken()
      const dateStr = date.toISOString().split('T')[0]

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.HEALTH.WEIGHT_RECORDS}?date=${dateStr}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (response.ok) {
        const data = await response.json()
        const mappedRecords = data.map((record: any) => ({
          id: record.id,
          cowId: record.cowId,
          cowName: record.cow?.name || '',
          cowCode: record.cow?.cowCode || '',
          cattleType: record.cow?.cattleType || '',
          date: record.date.split('T')[0],
          weight: record.weight,
          notes: record.notes || '',
        }))

        // Update allRecords with new data
        // This will trigger the useEffect below to update cowWeights
        if (selectedCattleType && allCows.length > 0) {
          const cowsInGroup = allCows.filter((cow) => cow.cattleType === selectedCattleType)
          const weights = cowsInGroup.map((cow) => {
            const existingRecord = mappedRecords.find(
              (record: WeightRecord) => record.cowId === cow.id
            )
            return {
              cowId: cow.id,
              cowName: cow.name || '',
              cowCode: cow.cowCode,
              cattleType: cow.cattleType,
              weight: existingRecord?.weight || 0,
              notes: existingRecord?.notes || '',
              existingRecordId: existingRecord?.id,
            }
          })
          setCowWeights(weights)
        }
      }
    } catch (error) {
      console.error('Error fetching weight records:', error)
    }
  }

  const fetchCows = async () => {
    try {
      setIsLoadingCows(true)
      const token = storageService.getAuthToken()

      // Fetch all cows without pagination limit
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.COWS.BASE}?limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const result = await response.json()
        // API returns { data: [...], pagination: {...} }
        setAllCows(result.data || [])
      }
    } catch (error) {
      console.error('Error fetching cows:', error)
    } finally {
      setIsLoadingCows(false)
    }
  }

  // Trigger fetch when allCows is loaded
  useEffect(() => {
    if (selectedCattleType && allCows.length > 0 && formDate) {
      fetchWeightRecordsForDate(formDate)
    }
  }, [allCows])

  const handleWeightChange = (cowId: string, weight: number) => {
    setCowWeights((prev) =>
      prev.map((item) => (item.cowId === cowId ? { ...item, weight } : item))
    )
  }

  const handleNotesChange = (cowId: string, notes: string) => {
    setCowWeights((prev) =>
      prev.map((item) => (item.cowId === cowId ? { ...item, notes } : item))
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCattleType) {
      alert('กรุณาเลือกกลุ่มวัว')
      return
    }

    // กรองเฉพาะโคที่มีการกรอกน้ำหนัก (น้ำหนัก > 0)
    const recordsToSave = cowWeights
      .filter((item) => item.weight > 0)
      .map((item) => {
        const dateStr = formDate.toISOString().split('T')[0]
        return {
          id: item.existingRecordId || `${Date.now()}-${item.cowId}`,
          cowId: item.cowId,
          cowName: item.cowName,
          cowCode: item.cowCode,
          cattleType: item.cattleType,
          date: dateStr,
          weight: item.weight,
          notes: item.notes,
        }
      })

    if (recordsToSave.length === 0) {
      alert('กรุณากรอกน้ำหนักอย่างน้อย 1 ตัว')
      return
    }

    onSave(recordsToSave, formDate)
  }

  const getCattleTypeLabel = (type: string) => {
    return CATTLE_TYPES.find((t) => t.value === type)?.label || type
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-text-primary">
          บันทึกน้ำหนักโค - {getCattleTypeLabel(selectedCattleType)}
        </h2>
        <button
          onClick={onClose}
          className="text-text-tertiary hover:text-text-primary transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date & Cattle Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              วันที่ <span className="text-error">*</span>
            </label>
            <input
              type="date"
              value={formDate.toISOString().split('T')[0]}
              onChange={(e) => setFormDate(new Date(e.target.value))}
              required
              className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              กลุ่มวัว <span className="text-error">*</span>
            </label>
            <select
              value={selectedCattleType}
              onChange={(e) => setSelectedCattleType(e.target.value)}
              required
              className="w-full px-4 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800"
            >
              <option value="">-- เลือกกลุ่มวัว --</option>
              {CATTLE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Cows Weight Table */}
        {selectedCattleType && cowWeights.length > 0 && (
          <div className="border border-border-light rounded-lg overflow-hidden">
            <div className="bg-primary-50 px-4 py-3 border-b border-primary-200">
              <h3 className="font-semibold text-primary-800 flex items-center gap-2">
                ⚖️ บันทึกน้ำหนักโคทั้งหมด ({cowWeights.length} ตัว)
              </h3>
              <p className="text-xs text-text-tertiary mt-1">
                กรอกน้ำหนักเฉพาะตัวที่ต้องการบันทึก (เว้นว่างหรือใส่ 0 ถ้าไม่ต้องการบันทึก)
              </p>
            </div>

            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-background-tertiary sticky top-0">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary border-b border-border-light">
                      ลำดับ
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary border-b border-border-light">
                      รหัสโค
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary border-b border-border-light">
                      ชื่อโค
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary border-b border-border-light">
                      น้ำหนัก (กก.) <span className="text-error">*</span>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-text-secondary border-b border-border-light">
                      หมายเหตุ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cowWeights.map((cow, index) => (
                    <tr
                      key={cow.cowId}
                      className="border-b border-border-light hover:bg-background-secondary transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-text-secondary">{index + 1}</td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-text-primary">
                          {cow.cowCode}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-text-primary">{cow.cowName}</span>
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={cow.weight || ''}
                          onChange={(e) =>
                            handleWeightChange(cow.cowId, parseFloat(e.target.value) || 0)
                          }
                          className="w-32 px-3 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 text-sm"
                          placeholder="0"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="text"
                          value={cow.notes}
                          onChange={(e) => handleNotesChange(cow.cowId, e.target.value)}
                          className="w-full px-3 py-2 border border-border-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-800 text-sm"
                          placeholder="หมายเหตุ..."
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedCattleType && cowWeights.length === 0 && !isLoadingCows && (
          <div className="text-center py-8 border border-border-light rounded-lg bg-background-secondary">
            <p className="text-text-secondary">ไม่มีโคในกลุ่ม {getCattleTypeLabel(selectedCattleType)}</p>
          </div>
        )}

        {isLoadingCows && (
          <div className="text-center py-8">
            <p className="text-text-secondary">กำลังโหลดข้อมูล...</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-light">
          <Button type="button" variant="secondary" onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex items-center gap-2"
            disabled={!selectedCattleType}
          >
            <Save className="w-4 h-4" />
            <span>บันทึกทั้งหมด</span>
          </Button>
        </div>
      </form>
    </Modal>
  )
}
